// MobiRoute Routes Page - Refactored with Clean Code Principles
// Separated concerns: Hooks for logic, Components for UI

import React, { useState, useMemo, useEffect } from 'react';
import { Toast } from 'devextreme-react/toast';
import Button from 'devextreme-react/button';
import Splitter, { Item } from 'devextreme-react/splitter';

// Components
import MapComponent from './components/MapComponent';
import { CustomDataGrid } from '../Customer/CustomDataGrid';
import { MapTypeSelector } from './components/MapTypeSelector';
import { RoutesSidebar } from './components/RoutesSidebar';
import { SummarySidebar } from './components/SummarySidebar';
import { TimelineView } from './components/TimelineView';
import { VRPConstraintsModal, VRPConstraints } from './components/VRPConstraintsModal';
import { DepotSettingsModal, DepotSettings } from './components/DepotSettingsModal';

// Hooks
import {
  useToast,
  useCustomerManagement,
  useVehicleManagement,
  useRouteManagement,
  useManualRouting
} from './hooks';

// Styles
import './RoutesPage.css';

// Constants
import { DEFAULT_VALUES } from '../../../api/mobiApi/constants/app.constants';

const RoutesPageNew: React.FC = () => {
  // UI State
  const [mapType, setMapType] = useState<string>('osm');
  const [activeTab, setActiveTab] = useState<'orders' | 'vehicles' | 'timeline' | 'routes'>('orders');
  const [isDataGridCollapsed, setIsDataGridCollapsed] = useState<boolean>(false);
  const [isRoutesSidebarCollapsed, setIsRoutesSidebarCollapsed] = useState<boolean>(false);
  const [isSummarySidebarCollapsed, setIsSummarySidebarCollapsed] = useState<boolean>(false);
  const [isConstraintsModalVisible, setIsConstraintsModalVisible] = useState<boolean>(false);
  const [isDepotSettingsModalVisible, setIsDepotSettingsModalVisible] = useState<boolean>(false);
  const [focusLocation, setFocusLocation] = useState<{ lat: number; lon: number; zoom?: number } | null>(null);

  // Load depot settings from localStorage or use defaults
  const getInitialDepotSettings = (): DepotSettings => {
    try {
      const savedSettings = localStorage.getItem('depotSettings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        console.log('📍 Depo ayarları localStorage\'dan yüklendi:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('❌ Depo ayarları yüklenirken hata:', error);
    }

    // Default depot settings
    return {
      start_depot: {
        lat: DEFAULT_VALUES.MAP_CENTER[0],
        lon: DEFAULT_VALUES.MAP_CENTER[1],
        name: 'Ana Depo',
        demand: 0,
        address: ''
      },
      end_depot: {
        lat: DEFAULT_VALUES.MAP_CENTER[0],
        lon: DEFAULT_VALUES.MAP_CENTER[1],
        name: 'Ana Depo',
        demand: 0,
        address: ''
      },
      use_same_depot: true
    };
  };

  // Depot Settings State
  const [depotSettings, setDepotSettings] = useState<DepotSettings>(getInitialDepotSettings());

  // Save depot settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('depotSettings', JSON.stringify(depotSettings));
      console.log('💾 Depo ayarları localStorage\'a kaydedildi');
    } catch (error) {
      console.error('❌ Depo ayarları kaydedilirken hata:', error);
    }
  }, [depotSettings]);

  // Load VRP constraints from localStorage or use defaults
  const getInitialVRPConstraints = (): VRPConstraints => {
    try {
      const savedConstraints = localStorage.getItem('vrpConstraints');
      if (savedConstraints) {
        const parsed = JSON.parse(savedConstraints);
        console.log('⚙️ VRP kısıtlamaları localStorage\'dan yüklendi:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('❌ VRP kısıtlamaları yüklenirken hata:', error);
    }

    // Default VRP constraints
    return {
      // Kısıtlamaları Kaldır
      ignoreConstraints: false,

      // Çalışma Saatleri
      startTime: '08:00',
      endTime: '18:00',
      serviceDuration: 2400,

      // Öğle Molası
      lunchBreak: true,
      lunchBreakStart: '12:30',
      lunchBreakEnd: '13:30',

      // 1. Zaman & Mesafe Kısıtlamaları
      maxWorkingHours: 0,      // 0 = limitsiz
      maxDistance: 0,          // 0 = limitsiz, min 500 km

      // 2. Görev Kısıtlamaları
      maxTasks: null,

      // 3. Mola Kısıtlamaları
      enableBreaks: true,
      breakDuration: 30,
      breakAfterHours: 4,

      // 4. Öncelik Kısıtlamaları
      enablePriority: false,
      priorityWeight: 5,

      // 5. Yetenek/Beceri Kısıtlamaları
      enableSkills: false,

      // 6. Hız Kısıtlamaları
      speedFactor: 1.0,

      // 7. Yol Kısıtlamaları
      avoidTolls: false,
      avoidHighways: false,
      avoidTunnels: false,
      avoidFerries: false,

      // 8. Optimizasyon Hedefi
      optimizationGoal: 'balanced'
    };
  };

  const [vrpConstraints, setVrpConstraints] = useState<VRPConstraints>(getInitialVRPConstraints());

  // Save VRP constraints to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('vrpConstraints', JSON.stringify(vrpConstraints));
      console.log('💾 VRP kısıtlamaları localStorage\'a kaydedildi');
    } catch (error) {
      console.error('❌ VRP kısıtlamaları kaydedilirken hata:', error);
    }
  }, [vrpConstraints]);

  // Toast Hook
  const { toastConfig, showToast, hideToast } = useToast();

  // Customer Management Hook
  const customerManagement = useCustomerManagement();

  // Vehicle Management Hook
  const vehicleManagement = useVehicleManagement();

  // Route Management Hook
  const routeManagement = useRouteManagement({
    selectedCustomers: customerManagement.selectedCustomers,
    selectedVehicles: vehicleManagement.selectedVehicles,
    depot: depotSettings.start_depot,
    endDepot: depotSettings.use_same_depot ? undefined : depotSettings.end_depot,
    showToast,
    constraints: vrpConstraints
  });

  // Manual Routing Hook
  const manualRouting = useManualRouting({
    selectedVehicles: vehicleManagement.selectedVehicles,
    selectedCustomers: customerManagement.selectedCustomers,
    routes: routeManagement.routes,
    depot: depotSettings.start_depot,
    showToast,
    setRoutes: routeManagement.setRoutes,
    setSelectedRoute: routeManagement.setSelectedRoute,
    setSelectedCustomers: customerManagement.setSelectedCustomers
  });

  // Calculate total capacity and demand
  const totalCapacity = useMemo(() => {
    return vehicleManagement.selectedVehicles.reduce((sum, vehicle) => sum + (vehicle.capacity || 0), 0);
  }, [vehicleManagement.selectedVehicles]);

  const totalDemand = useMemo(() => {
    return customerManagement.selectedCustomers.reduce((sum, customer) => sum + (customer.demand || 0), 0);
  }, [customerManagement.selectedCustomers]);

  // Confirmation modal state
  const [isConfirmingRoute, setIsConfirmingRoute] = useState<boolean>(false);

  // Event Handlers
  const handleSolveVRP = async () => {
    // Open constraints modal for confirmation before solving
    setIsConfirmingRoute(true);
    setIsConstraintsModalVisible(true);
  };

  // Handle route calculation after constraints confirmation
  const handleConfirmAndCalculate = async () => {
    setIsConstraintsModalVisible(false);
    setIsConfirmingRoute(false);

    const success = await routeManagement.solveVRP();
    if (success) {
      setActiveTab('timeline'); // Switch to timeline tab after solving
    }
  };

  // Handler for closing routes sidebar
  const handleCloseRoutesSidebar = () => {
    routeManagement.closeSelectedRoute();
  };

  // Handler for customer click in sidebar - focus on map
  const handleSidebarCustomerClick = (customer: any) => {
    if (customer.lat && customer.lon) {
      setFocusLocation({ lat: customer.lat, lon: customer.lon, zoom: 17 });
    } else if (customer.latitude && customer.longitude) {
      setFocusLocation({ lat: customer.latitude, lon: customer.longitude, zoom: 17 });
    }
  };

  // Handler for inline time window editing in marker popup
  const handleTimeWindowChange = (customerId: string, timeWindow: { start: string; end: string } | undefined) => {
    const updatedCustomers = customerManagement.selectedCustomers.map(c =>
      c.id === customerId
        ? { ...c, time_window: timeWindow }
        : c
    );
    customerManagement.setSelectedCustomers(updatedCustomers);

    if (timeWindow) {
      showToast('success', 'Saat aralığı kaydedildi');
    } else {
      showToast('info', 'Saat aralığı kaldırıldı');
    }
  };

  // Handler for inline priority editing in marker popup
  const handlePriorityChange = (customerId: string, priority: number | undefined) => {
    const updatedCustomers = customerManagement.selectedCustomers.map(c =>
      c.id === customerId
        ? { ...c, priority: priority }
        : c
    );
    customerManagement.setSelectedCustomers(updatedCustomers);

    if (priority !== undefined) {
      showToast('success', `Öncelik ${priority} olarak kaydedildi`);
    } else {
      showToast('info', 'Öncelik kaldırıldı');
    }
  };

  // Reset tab to orders when routes are cleared
  useEffect(() => {
    if (routeManagement.routes.length === 0 && (activeTab === 'timeline' || activeTab === 'routes')) {
      setActiveTab('orders');
    }
  }, [routeManagement.routes.length, activeTab]);

  // Handle window resize - force map invalidation
  useEffect(() => {
    const handleResize = () => {
      // Small delay to ensure splitter has finished resizing
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`fullscreen-map-container ${manualRouting.isManualRoutingMode ? 'manual-routing-active' : ''}`}>
      {/* Toast Notification */}
      <Toast
        visible={toastConfig.visible}
        message={toastConfig.message}
        type={toastConfig.type}
        onHiding={hideToast}
        displayTime={3000}
      />

      {/* Splitter between Map and DataGrid */}
      <Splitter
        orientation="vertical"
        style={{ width: '100%' }}
        onResizeEnd={() => {
          // Force map to recalculate size after splitter resize
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
          }, 50);
        }}
      >
        {/* Map Pane - Top 65% */}
        <Item size="65%" minSize="250px">
          {routeManagement.routes.length > 0 ? (
            <Splitter
              orientation="horizontal"
              onResizeEnd={() => {
                setTimeout(() => {
                  window.dispatchEvent(new Event('resize'));
                }, 50);
              }}
            >
              {/* Routes Sidebar - Left Panel */}
              <Item
                size={routeManagement.selectedRoute ? "280px" : "180px"}
                minSize="180px"
                maxSize="450px"
                resizable={true}
                collapsed={isRoutesSidebarCollapsed}
              >
                <RoutesSidebar
                  routes={routeManagement.routes}
                  selectedRoute={routeManagement.selectedRoute}
                  onRouteClick={routeManagement.handleRouteClick}
                  onCloseRoute={handleCloseRoutesSidebar}
                  onCustomerClick={handleSidebarCustomerClick}
                />
              </Item>

              {/* Map Area + Summary Sidebar */}
              <Item>
                <Splitter
                  orientation="horizontal"
                  onResizeEnd={() => {
                    setTimeout(() => {
                      window.dispatchEvent(new Event('resize'));
                    }, 50);
                  }}
                >
                  {/* Map */}
                  <Item>
                    <div className="map-fullscreen">
                      <MapComponent
                        customers={customerManagement.selectedCustomers}
                        routes={routeManagement.routes}
                        startDepot={depotSettings.start_depot}
                        endDepot={depotSettings.use_same_depot ? undefined : depotSettings.end_depot}
                        center={[depotSettings.start_depot.lat, depotSettings.start_depot.lon]}
                        zoom={12}
                        mapType={mapType}
                        onRouteClick={routeManagement.handleRouteClick}
                        selectedRouteId={routeManagement.selectedRoute?.vehicle_id}
                        isEditMode={manualRouting.isManualRoutingMode}
                        drawingMode={manualRouting.drawingMode}
                        isMultiAreaMode={
                          manualRouting.manualRoutingMode === 'multi-move' ||
                          manualRouting.manualRoutingMode === 'area-polygon'
                        }
                        onCustomersSelect={manualRouting.handleCustomersSelectedByRectangle}
                        onTimeWindowChange={handleTimeWindowChange}
                        onPriorityChange={handlePriorityChange}
                        focusLocation={focusLocation}
                      />
                      <MapTypeSelector
                        selectedMapType={mapType}
                        onMapTypeChange={setMapType}
                      />
                      {/* Floating Routes Sidebar Toggle Button - Left Top */}
                      <Button
                        icon={isRoutesSidebarCollapsed ? "chevronright" : "chevronleft"}
                        onClick={() => setIsRoutesSidebarCollapsed(!isRoutesSidebarCollapsed)}
                        type="default"
                        stylingMode="contained"
                        hint={isRoutesSidebarCollapsed ? "Rotaları Genişlet" : "Rotaları Küçült"}
                        style={{
                          position: 'absolute',
                          top: '20px',
                          left: '20px',
                          zIndex: 1000,
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
                        }}
                      />
                      {/* Floating Summary Sidebar Toggle Button - Right Top */}
                      <Button
                        icon={isSummarySidebarCollapsed ? "chevronleft" : "chevronright"}
                        onClick={() => setIsSummarySidebarCollapsed(!isSummarySidebarCollapsed)}
                        type="default"
                        stylingMode="contained"
                        hint={isSummarySidebarCollapsed ? "Özeti Genişlet" : "Özeti Küçült"}
                        style={{
                          position: 'absolute',
                          top: '20px',
                          right: '20px',
                          zIndex: 1000,
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
                        }}
                      />
                      {/* Floating DataGrid Toggle Button - Bottom Right */}
                      <Button
                        icon={isDataGridCollapsed ? "chevronup" : "chevrondown"}
                        onClick={() => setIsDataGridCollapsed(!isDataGridCollapsed)}
                        type="default"
                        stylingMode="contained"
                        hint={isDataGridCollapsed ? "Tabloyu Genişlet" : "Tabloyu Küçült"}
                        style={{
                          position: 'absolute',
                          bottom: '20px',
                          right: '20px',
                          zIndex: 1000,
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
                        }}
                      />
                    </div>
                  </Item>

                  {/* Summary Sidebar - Right */}
                  <Item size="120px" minSize="120px" maxSize="500px" resizable={true} collapsed={isSummarySidebarCollapsed}>
                    <SummarySidebar
                      selectedRoute={routeManagement.selectedRoute}
                      routes={routeManagement.routes}
                      selectedCustomersCount={customerManagement.selectedCustomers.length}
                      selectedVehicles={vehicleManagement.selectedVehicles}
                      solutionStats={routeManagement.solutionStats}
                    />
                  </Item>
                </Splitter>
              </Item>
            </Splitter>
          ) : (
            <div className="map-fullscreen">
              <MapComponent
                customers={customerManagement.selectedCustomers}
                routes={routeManagement.routes}
                startDepot={depotSettings.start_depot}
                endDepot={depotSettings.use_same_depot ? undefined : depotSettings.end_depot}
                center={[depotSettings.start_depot.lat, depotSettings.start_depot.lon]}
                zoom={12}
                mapType={mapType}
                onRouteClick={routeManagement.handleRouteClick}
                selectedRouteId={routeManagement.selectedRoute?.vehicle_id}
                isEditMode={manualRouting.isManualRoutingMode}
                drawingMode={manualRouting.drawingMode}
                isMultiAreaMode={
                  manualRouting.manualRoutingMode === 'multi-move' ||
                  manualRouting.manualRoutingMode === 'area-polygon'
                }
                onCustomersSelect={manualRouting.handleCustomersSelectedByRectangle}
                onTimeWindowChange={handleTimeWindowChange}
                onPriorityChange={handlePriorityChange}
                focusLocation={focusLocation}
              />
              <MapTypeSelector
                selectedMapType={mapType}
                onMapTypeChange={setMapType}
              />
              {/* Floating DataGrid Toggle Button */}
              <Button
                icon={isDataGridCollapsed ? "chevronup" : "chevrondown"}
                onClick={() => setIsDataGridCollapsed(!isDataGridCollapsed)}
                type="default"
                stylingMode="contained"
                hint={isDataGridCollapsed ? "Tabloyu Genişlet" : "Tabloyu Küçült"}
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  right: '20px',
                  zIndex: 1000,
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
                }}
              />
            </div>
          )}
        </Item>

        {/* DataGrid Pane - Bottom 45% */}
        <Item size="45%" minSize="280px" collapsed={isDataGridCollapsed}>
          <div className="datagrid-section-splitter">
            {/* Tab Headers with Action Buttons */}
            <div className="datagrid-tabs">
              <div className="datagrid-tabs-left">
                <div
                  className={`datagrid-tab ${activeTab === 'orders' ? 'active' : ''}`}
                  onClick={() => setActiveTab('orders')}
                >
                  <i className="pi pi-shopping-cart"></i>
                  <span>Orders</span>
                  <span className="tab-count">{customerManagement.selectedCustomers.length}/{customerManagement.allCustomers.length}</span>
                </div>
                <div
                  className={`datagrid-tab ${activeTab === 'vehicles' ? 'active' : ''}`}
                  onClick={() => setActiveTab('vehicles')}
                >
                  <i className="pi pi-truck"></i>
                  <span>Araçlar</span>
                  <span className="tab-count">{vehicleManagement.selectedVehicles.length}/{vehicleManagement.allVehicles.length}</span>
                </div>
                {routeManagement.routes.length > 0 && (
                  <>
                    <div
                      className={`datagrid-tab ${activeTab === 'timeline' ? 'active' : ''}`}
                      onClick={() => setActiveTab('timeline')}
                    >
                      <i className="pi pi-list"></i>
                      <span>Timeline</span>
                      <span className="tab-count">{routeManagement.routes.length}</span>
                    </div>
                    <div
                      className={`datagrid-tab ${activeTab === 'routes' ? 'active' : ''}`}
                      onClick={() => setActiveTab('routes')}
                    >
                      <i className="pi pi-map"></i>
                      <span>Rotalar</span>
                      <span className="tab-count">
                        {routeManagement.selectedRoute?.route_sequence?.filter((s: any) => s.location_type !== 'DEPOT' && s.location_type !== 'depot').length || 0}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className="datagrid-toolbar-actions">
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    text="Depo Ayarları"
                    icon="home"
                    onClick={() => setIsDepotSettingsModalVisible(true)}
                    type="normal"
                    stylingMode="outlined"
                    className="toolbar-action-btn"
                    hint="Depo konumunu ve bilgilerini ayarla"
                  />
                  <Button
                    text={routeManagement.loading ? "Hesaplanıyor..." : "Rotayı Hesapla"}
                    icon={routeManagement.loading ? "refresh" : "check"}
                    onClick={handleSolveVRP}
                    disabled={customerManagement.selectedCustomers.length === 0 || vehicleManagement.selectedVehicles.length === 0 || routeManagement.loading}
                    type="success"
                    stylingMode="contained"
                    className="toolbar-action-btn"
                  />
                  <Button
                    text={manualRouting.isManualRoutingMode ? "Manuel Rotalamayı Kapat" : "Manuel Rotalama"}
                    icon={manualRouting.isManualRoutingMode ? "close" : "edit"}
                    onClick={manualRouting.toggleManualRouting}
                    type={manualRouting.isManualRoutingMode ? "default" : "normal"}
                    stylingMode="contained"
                    className="toolbar-action-btn"
                  />
                </div>
              </div>
            </div>

            {/* DataGrid Content */}
            <div className="datagrid-content">
              {activeTab === 'orders' && (
                <>
                  <div className="info-bar-fixed" style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    borderBottom: '2px solid #fbbf24',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#92400e'
                  }}>
                    <i className="pi pi-box" style={{ fontSize: '18px' }}></i>
                    <span>Toplam Yük: {totalDemand} kg</span>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: '400' }}>
                      ({customerManagement.selectedCustomers.length} müşteri seçili)
                    </span>
                  </div>
                  <CustomDataGrid
                    data={customerManagement.allCustomers}
                    columns={[
                      { dataField: 'name', caption: 'Müşteri Adı', width: 200 },
                      { dataField: 'address', caption: 'Adres', width: 250 },
                      { dataField: 'demand', caption: 'Talep (kg)', dataType: 'number', width: 120, alignment: 'center' },
                      { dataField: 'lat', caption: 'Enlem', dataType: 'number', width: 120 },
                      { dataField: 'lon', caption: 'Boylam', dataType: 'number', width: 120 }
                    ]}
                    keyExpr="id"
                    selectedRowKeys={customerManagement.selectedCustomers.map(c => c.id)}
                    onSelectionChanged={(keys) => {
                      const selected = customerManagement.allCustomers.filter(c => keys.includes(c.id));
                      customerManagement.setSelectedCustomers(selected);
                    }}
                    searchPlaceholder="Müşteri ara..."
                    noDataText="Müşteri bulunamadı"
                    columnChooserEnabled={true}
                    t={(key) => key}
                  />
                </>
              )}
              {activeTab === 'vehicles' && (
                <>
                  <div className="info-bar-fixed" style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                    borderBottom: '2px solid #3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1e3a8a'
                  }}>
                    <i className="pi pi-truck" style={{ fontSize: '18px' }}></i>
                    <span>Toplam Kapasite: {totalCapacity} kg</span>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: '400' }}>
                      ({vehicleManagement.selectedVehicles.length} araç seçili)
                    </span>
                  </div>
                  <CustomDataGrid
                    data={vehicleManagement.allVehicles}
                    columns={[
                      { dataField: 'name', caption: 'Araç Adı', width: 200 },
                      { dataField: 'type', caption: 'Tip', width: 150 },
                      { dataField: 'capacity', caption: 'Kapasite (kg)', dataType: 'number', width: 150, alignment: 'center' }
                    ]}
                    keyExpr="id"
                    selectedRowKeys={vehicleManagement.selectedVehicles.map(v => v.id)}
                    onSelectionChanged={(keys) => {
                      const selected = vehicleManagement.allVehicles.filter(v => keys.includes(v.id));
                      vehicleManagement.setSelectedVehicles(selected);
                    }}
                    searchPlaceholder="Araç ara..."
                    noDataText="Araç bulunamadı"
                    columnChooserEnabled={true}
                    t={(key) => key}
                  />
                </>
              )}
              {activeTab === 'timeline' && (
                <TimelineView
                  routes={routeManagement.routes}
                  loading={routeManagement.loading}
                  onOptimizeWithAI={routeManagement.optimizeWithAI}
                  onSetSelectedRoute={routeManagement.setSelectedRoute}
                  onSetRoutes={routeManagement.setRoutes}
                  showToast={showToast}
                />
              )}
              {activeTab === 'routes' && (
                <>
                  {routeManagement.selectedRoute ? (
                    <>
                      <div className="info-bar-fixed" style={{
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%)',
                        borderBottom: '2px solid #8b5cf6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#4c1d95'
                      }}>
                        <i className="pi pi-car" style={{ fontSize: '18px' }}></i>
                        <span>{routeManagement.selectedRoute.vehicle_id || 'Rota'} - Müşteri Sıralaması</span>
                        <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: '400' }}>
                          ({routeManagement.selectedRoute.route_sequence?.filter((s: any) => s.location_type !== 'DEPOT' && s.location_type !== 'depot').length || 0} müşteri)
                        </span>
                      </div>
                      <CustomDataGrid
                        data={routeManagement.selectedRoute.route_sequence?.filter((s: any) => s.location_type !== 'DEPOT' && s.location_type !== 'depot').map((step: any, index: number) => {
                          // Helper function to truncate time to HH:MM format
                          const formatTime = (time: string | undefined) => {
                            if (!time) return '-';
                            // If time is in HH:MM:SS format, truncate to HH:MM
                            const parts = time.split(':');
                            if (parts.length >= 2) {
                              return `${parts[0]}:${parts[1]}`;
                            }
                            return time;
                          };

                          return {
                            id: step.customer_id || step.id || index,
                            sequence: index + 1,
                            name: step.customer_name || step.name || `Müşteri ${index + 1}`,
                            demand: step.demand || 0,
                            arrival_time: formatTime(step.arrival_time_formatted),
                            end_time: formatTime(step.end_time_formatted),
                            service_duration: step.service_duration ? `${Math.round(step.service_duration / 60)} dk` : '-',
                            waiting_time: formatTime(step.waiting_time_formatted),
                            distance_from_prev: step.valhalla_distance_km ? `${step.valhalla_distance_km.toFixed(1)} km` : '-',
                            travel_time: formatTime(step.valhalla_time_formatted),
                            cumulative_distance: step.cumulative_distance ? `${(step.cumulative_distance / 1000).toFixed(1)} km` : '-',
                            current_load: step.current_load || 0,
                            lat: step.lat,
                            lon: step.lon
                          };
                        }) || []}
                        columns={[
                          { dataField: 'sequence', caption: 'Sıra', width: 50, alignment: 'center' },
                          { dataField: 'name', caption: 'Müşteri Adı', width: 150 },
                          { dataField: 'demand', caption: 'Talep', dataType: 'number', width: 70, alignment: 'center' },
                          { dataField: 'arrival_time', caption: 'Varış', width: 60, alignment: 'center' },
                          { dataField: 'end_time', caption: 'Bitiş', width: 60, alignment: 'center' },
                          { dataField: 'service_duration', caption: 'Servis', width: 60, alignment: 'center' },
                          { dataField: 'waiting_time', caption: 'Bekleme', width: 60, alignment: 'center' },
                          { dataField: 'distance_from_prev', caption: 'Mesafe', width: 70, alignment: 'center' },
                          { dataField: 'travel_time', caption: 'Seyahat', width: 60, alignment: 'center' },
                          { dataField: 'cumulative_distance', caption: 'Toplam Mesafe', width: 100, alignment: 'center' },
                          { dataField: 'current_load', caption: 'Kalan Yük', dataType: 'number', width: 80, alignment: 'center' },
                          { dataField: 'lat', caption: 'Enlem', dataType: 'number', width: 90 },
                          { dataField: 'lon', caption: 'Boylam', dataType: 'number', width: 90 }
                        ]}
                        keyExpr="id"
                        selectedRowKeys={[]}
                        onSelectionChanged={() => {}}
                        searchPlaceholder="Müşteri ara..."
                        noDataText="Müşteri bulunamadı"
                        columnChooserEnabled={true}
                        t={(key) => key}
                      />
                    </>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: '#94a3b8',
                      gap: '16px'
                    }}>
                      <i className="pi pi-map" style={{ fontSize: '64px', opacity: 0.3 }}></i>
                      <p style={{ fontSize: '16px', fontWeight: '500' }}>Rota seçilmedi</p>
                      <p style={{ fontSize: '14px' }}>Haritadan bir rota seçerek müşteri sıralamasını görüntüleyin</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Item>
      </Splitter>

      {/* Vehicle Selection Popup for Manual Routing */}
      {manualRouting.showVehicleSelectionPopup && (
        <div className="popup-overlay" onClick={() => manualRouting.setShowVehicleSelectionPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Araç Seçin</h3>
              <Button
                icon="close"
                onClick={() => manualRouting.setShowVehicleSelectionPopup(false)}
                stylingMode="text"
              />
            </div>
            <div className="popup-body">
              <p className="popup-info">
                {manualRouting.selectedCustomersForManualRoute.length} müşteri seçildi. Lütfen bu müşterileri atamak istediğiniz aracı seçin:
              </p>
              <div className="vehicle-selection-list">
                {vehicleManagement.selectedVehicles.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className="vehicle-selection-item"
                    onClick={() => manualRouting.assignCustomersToVehicle(vehicle.id)}
                  >
                    <div className="vehicle-icon">
                      <i className="pi pi-truck"></i>
                    </div>
                    <div className="vehicle-info">
                      <div className="vehicle-name">{vehicle.name}</div>
                      <div className="vehicle-details">
                        Kapasite: {vehicle.capacity} kg
                      </div>
                    </div>
                    <i className="pi pi-angle-right"></i>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Depot Settings Modal */}
      <DepotSettingsModal
        visible={isDepotSettingsModalVisible}
        onHide={() => setIsDepotSettingsModalVisible(false)}
        onSave={(settings) => {
          setDepotSettings(settings);
          if (settings.use_same_depot) {
            showToast('success', 'Depo ayarları kaydedildi (başlangıç ve bitiş aynı)');
          } else {
            showToast('success', 'Depo ayarları kaydedildi (farklı başlangıç ve bitiş)');
          }
          console.log('Depo Ayarları:', settings);
        }}
        initialSettings={depotSettings}
      />

      {/* VRP Constraints Modal */}
      <VRPConstraintsModal
        visible={isConstraintsModalVisible}
        mode={isConfirmingRoute ? 'confirm' : 'edit'}
        onHide={() => {
          setIsConstraintsModalVisible(false);
          setIsConfirmingRoute(false);
        }}
        onSave={(constraints) => {
          setVrpConstraints(constraints);
          if (!isConfirmingRoute) {
            showToast('success', 'Kısıtlamalar kaydedildi');
          }
          console.log('VRP Kısıtlamaları:', constraints);
        }}
        onConfirm={handleConfirmAndCalculate}
        initialConstraints={vrpConstraints}
      />

      {/* Toast Notification */}
      <Toast
        visible={toastConfig.visible}
        message={toastConfig.message}
        type={toastConfig.type}
        onHiding={hideToast}
        displayTime={3000}
      />
    </div>
  );
};

export default RoutesPageNew;
